import React, { useEffect, useRef, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import RichText, { detectDir } from "./RichText";
import LinkEmbed from "./LinkEmbed";
import { TactLoader } from "../components/Tactical";
import { toast } from "sonner";
import { Send } from "lucide-react";

export default function LeagueChat({ leagueId }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const endRef = useRef(null);
  const sinceRef = useRef(null);

  const scrollToBottom = () => endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });

  const fetchMessages = useCallback(async (initial = false) => {
    try {
      const params = sinceRef.current ? { since: sinceRef.current } : {};
      const { data } = await api.get(`/leagues/${leagueId}/chat`, { params });
      if (data.length) {
        sinceRef.current = data[data.length - 1].created_at;
        setMessages((prev) => (initial ? data : [...prev, ...data]));
        setTimeout(scrollToBottom, 50);
      } else if (initial) {
        setMessages([]);
      }
    } catch (e) { /* noop */ }
    finally { if (initial) setLoading(false); }
  }, [leagueId]);

  useEffect(() => {
    sinceRef.current = null;
    setMessages([]);
    setLoading(true);
    fetchMessages(true);
    const iv = setInterval(() => fetchMessages(false), 5000);
    return () => clearInterval(iv);
  }, [fetchMessages]);

  const onSend = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/leagues/${leagueId}/chat`, { text: text.trim() });
      setMessages((prev) => [...prev, data]);
      sinceRef.current = data.created_at;
      setText("");
      setTimeout(scrollToBottom, 50);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] h-[60vh] flex flex-col" data-testid="league-chat">
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {loading ? <TactLoader label="LOADING CHAT" /> : (
          messages.length === 0 ? (
            <div className="font-mono text-[#71717A] text-sm text-center mt-10">[ NO MESSAGES YET — break the ice, captain. ]</div>
          ) : messages.map((m) => {
            const mine = m.user_id === user?.id;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`group-msg-${m.id}`}>
                <div className={`max-w-[75%] px-4 py-2 border ${mine ? "border-[#7C3AED]/40 bg-[#7C3AED]/10" : "border-[#E4E4E7] dark:border-[#2A2A30] bg-[#FAFAFC] dark:bg-[#09090B]"} text-[#09090B] dark:text-[#F4F4F5]`}>
                  {!mine && <div className="font-mono text-[10px] text-[#7C3AED] uppercase tracking-[0.2em] mb-1">{m.username}</div>}
                  <div className="text-sm whitespace-pre-wrap break-words"><RichText text={m.text} /></div>
                  <div className="mt-2"><LinkEmbed text={m.text} /></div>
                  <div className="font-mono text-[9px] text-[#71717A] tracking-[0.2em] mt-1">{new Date(m.created_at).toLocaleString()}</div>
                </div>
              </div>
            );
          })
        )}
        <div ref={endRef} />
      </div>
      <form onSubmit={onSend} className="border-t border-[#E4E4E7] dark:border-[#2A2A30] p-3 flex gap-2" data-testid="league-chat-form">
        <input
          data-testid="league-chat-input"
          value={text} dir={detectDir(text) === "rtl" ? "rtl" : "auto"} onChange={(e) => setText(e.target.value)}
          className="flex-1 bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-4 py-2 text-[#09090B] dark:text-[#F4F4F5] font-body focus:outline-none focus:ring-1 focus:ring-[#7C3AED] focus:border-[#7C3AED]"
          placeholder="Message everyone in this league…"
          maxLength={2000}
        />
        <button data-testid="league-chat-send-btn" type="submit" disabled={sending || !text.trim()} className="bg-[#7C3AED] text-white font-heading uppercase tracking-widest px-4 py-2 hover:bg-[#6D28D9] transition-colors disabled:opacity-50 inline-flex items-center gap-2">
          <Send className="w-3 h-3" /> Send
        </button>
      </form>
    </div>
  );
}
